// ═══════════════════════════════════════════════════════════════
// V·IANet Governance — Modal for Creating a New Puesto
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect } from 'react';
import { useI18n } from '../../../../i18n';
import {
  fetchDepartamentos,
  fetchOrganizaciones,
  fetchPuestos,
  fetchPersonas,
  fetchNivelesPuesto,
  fetchRolesFuncionales,
  crearPuesto,
  crearRelacion,
  crearAsignacion,
  type OrganizacionResumen,
  type NivelPuesto,
  type RolFuncional,
} from '../../../shared/services/api';

// 0474: el modal carga los niveles reales desde BD y envia nivelPuestoId (UUID).
// Antes de 0474 enviaba el slug con el nombre del campo equivocado (nivel) y el
// schema Zod del backend lo filtraba silenciosamente → puestos creados con NULLs.

interface CreatePuestoModalProps {
  defaultOrgId?: string;
  defaultReportaA?: string; // pre-fill "reporta a" for "crear subordinado"
  onCreated: () => void;
  onCancel: () => void;
}

export function CreatePuestoModal({
  defaultOrgId,
  defaultReportaA,
  onCreated,
  onCancel,
}: CreatePuestoModalProps) {
  const { t } = useI18n();
  const [titulo, setTitulo] = useState('');
  const [codigo, setCodigo] = useState('');
  const [nivelPuestoId, setNivelPuestoId] = useState('');
  const [rolFuncionalId, setRolFuncionalId] = useState('');
  const [departamentoId, setDepartamentoId] = useState('');
  const [organizacionId, setOrganizacionId] = useState(defaultOrgId ?? '');
  const [reportaA, setReportaA] = useState(defaultReportaA ?? '');
  const [personaId, setPersonaId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Data lists
  const [orgs, setOrgs] = useState<OrganizacionResumen[]>([]);
  const [deptos, setDeptos] = useState<
    Array<{ id: string; nombre: string; organizacionId?: string }>
  >([]);
  const [puestos, setPuestos] = useState<Array<{ id: string; titulo: string }>>([]);
  const [personas, setPersonas] = useState<
    Array<{ id: string; nombre: string; apellidos: string }>
  >([]);
  const [niveles, setNiveles] = useState<NivelPuesto[]>([]);
  const [rolesFuncionales, setRolesFuncionales] = useState<RolFuncional[]>([]);

  useEffect(() => {
    fetchOrganizaciones()
      .then((r) => setOrgs(r.data))
      .catch(() => {});
    fetchDepartamentos()
      .then((r) => setDeptos(r.data))
      .catch(() => {});
    fetchPuestos()
      .then((r) => setPuestos(r.data))
      .catch(() => {});
    fetchPersonas({ pageSize: '200' })
      .then((r) => setPersonas(r.data))
      .catch(() => {});
    fetchNivelesPuesto()
      .then((r) => {
        const data = r.data ?? [];
        setNiveles(data);
        // Default: nivel con slug STAFF si existe, o el de mayor orden (ultimo).
        const staff = data.find((n) => n.slug === 'STAFF');
        const fallback = data[data.length - 1];
        setNivelPuestoId((prev) => prev || staff?.id || fallback?.id || '');
      })
      .catch(() => {});
    fetchRolesFuncionales()
      .then((r) => setRolesFuncionales(r.data ?? []))
      .catch(() => {});
  }, []);

  const filteredDeptos = organizacionId
    ? deptos.filter((d) => d.organizacionId === organizacionId)
    : deptos;

  async function handleSave() {
    if (!titulo.trim()) {
      setError(t.structure.titleRequired);
      return;
    }
    if (!organizacionId) {
      setError('La organizacion es obligatoria');
      return;
    }
    setSaving(true);
    setError('');
    try {
      // 0474: el backend espera nivelPuestoId (UUID), no nivel (slug).
      const body: Record<string, unknown> = {
        titulo: titulo.trim(),
        organizacionId,
      };
      if (nivelPuestoId) body.nivelPuestoId = nivelPuestoId;
      if (rolFuncionalId) body.rolFuncionalId = rolFuncionalId;
      if (codigo.trim()) body.codigo = codigo.trim();
      if (departamentoId) body.departamentoId = departamentoId;

      const result = await crearPuesto(body);
      const newPuestoId = result.data?.id;

      // Create relation if reportaA is set
      if (reportaA && newPuestoId) {
        await crearRelacion({
          puestoPadreId: reportaA,
          puestoHijoId: newPuestoId,
          tipoRelacion: 'SOLIDA',
          esPrincipal: true,
        }).catch((e) => console.error('Error creating relation:', e));
      }

      // Assign persona if set
      if (personaId && newPuestoId) {
        await crearAsignacion({
          personaId,
          puestoId: newPuestoId,
          fte: 1,
          esPrincipal: true,
          fechaInicio: new Date().toISOString().slice(0, 10),
        }).catch((e) => console.error('Error creating assignment:', e));
      }

      onCreated();
    } catch (e: any) {
      setError(e.message ?? t.structure.errorCreatePuesto);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.4)',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 12,
          padding: '24px 28px',
          width: 480,
          maxWidth: '90vw',
          maxHeight: '85vh',
          overflowY: 'auto',
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
        }}
      >
        <h3
          style={{
            margin: '0 0 20px',
            fontSize: 16,
            fontWeight: 700,
            color: 'var(--color-primario)',
          }}
        >
          {t.structure.newPuestoTitle}
        </h3>

        {error && (
          <div
            style={{
              background: '#FEF2F2',
              color: '#DC2626',
              padding: '8px 12px',
              borderRadius: 6,
              fontSize: 12,
              marginBottom: 12,
            }}
          >
            {error}
          </div>
        )}

        {/* Titulo */}
        <label style={labelStyle}>{t.structure.tituloLabel} *</label>
        <input
          value={titulo}
          onChange={(e) => setTitulo(e.target.value)}
          placeholder="Ej: Director de Operaciones"
          style={inputStyle}
          autoFocus
        />

        {/* Codigo */}
        <label style={labelStyle}>{t.structure.codigoLabel}</label>
        <input
          value={codigo}
          onChange={(e) => setCodigo(e.target.value)}
          placeholder="Ej: DIR-OPS-001"
          style={inputStyle}
        />

        {/* Nivel */}
        <label style={labelStyle}>{t.structure.nivelLabel}</label>
        <select
          value={nivelPuestoId}
          onChange={(e) => setNivelPuestoId(e.target.value)}
          style={inputStyle}
        >
          <option value="">— {t.structure.selectOption} —</option>
          {niveles.map((n) => (
            <option key={n.id} value={n.id}>
              {n.nombreCorto ?? n.nombre}
            </option>
          ))}
        </select>

        {/* Rol funcional (opcional) */}
        <label style={labelStyle}>Rol funcional</label>
        <select
          value={rolFuncionalId}
          onChange={(e) => setRolFuncionalId(e.target.value)}
          style={inputStyle}
        >
          <option value="">— {t.structure.selectOption} —</option>
          {rolesFuncionales.map((r) => (
            <option key={r.id} value={r.id}>
              {r.codigo ? `${r.codigo} — ${r.nombre}` : r.nombre}
            </option>
          ))}
        </select>

        {/* Organizacion */}
        <label style={labelStyle}>{t.structure.organizacionLabel}</label>
        <select
          value={organizacionId}
          onChange={(e) => setOrganizacionId(e.target.value)}
          style={inputStyle}
        >
          <option value="">— {t.structure.selectOption} —</option>
          {orgs.map((o) => (
            <option key={o.id} value={o.id}>
              {o.nombre}
            </option>
          ))}
        </select>

        {/* Departamento */}
        <label style={labelStyle}>{t.structure.departamentoLabel}</label>
        <select
          value={departamentoId}
          onChange={(e) => setDepartamentoId(e.target.value)}
          style={inputStyle}
        >
          <option value="">— {t.structure.selectOption} —</option>
          {filteredDeptos.map((d) => (
            <option key={d.id} value={d.id}>
              {d.nombre}
            </option>
          ))}
        </select>

        {/* Reporta a */}
        <label style={labelStyle}>{t.structure.reportsToLabel}</label>
        <select value={reportaA} onChange={(e) => setReportaA(e.target.value)} style={inputStyle}>
          <option value="">— {t.structure.noneOption} —</option>
          {puestos.map((p) => (
            <option key={p.id} value={p.id}>
              {p.titulo}
            </option>
          ))}
        </select>

        {/* Persona asignada */}
        <label style={labelStyle}>{t.structure.assignedPersonLabel}</label>
        <select value={personaId} onChange={(e) => setPersonaId(e.target.value)} style={inputStyle}>
          <option value="">— {t.structure.noAssignment} —</option>
          {personas.map((p) => (
            <option key={p.id} value={p.id}>
              {p.nombre} {p.apellidos}
            </option>
          ))}
        </select>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
          <button onClick={onCancel} style={cancelBtnStyle}>
            {t.common.cancelar}
          </button>
          <button onClick={handleSave} disabled={saving} style={confirmBtnStyle}>
            {saving ? t.structure.creating : t.structure.createPuesto}
          </button>
        </div>
      </div>
    </div>
  );
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 11,
  fontWeight: 600,
  color: '#5A6171',
  marginBottom: 4,
  marginTop: 12,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  borderRadius: 6,
  border: '1px solid #D1D5DB',
  fontSize: 13,
  fontFamily: 'inherit',
  boxSizing: 'border-box',
};

const cancelBtnStyle: React.CSSProperties = {
  padding: '8px 16px',
  borderRadius: 6,
  border: '1px solid #D1D5DB',
  background: '#fff',
  cursor: 'pointer',
  fontSize: 12,
  fontFamily: 'inherit',
  color: '#5A6171',
};

const confirmBtnStyle: React.CSSProperties = {
  padding: '8px 16px',
  borderRadius: 6,
  border: 'none',
  background: 'var(--color-acento)',
  color: '#fff',
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: 600,
  fontFamily: 'inherit',
};
